import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { BiSearch, BiSolidInbox } from 'react-icons/bi';
import { listExamBookings } from '@/api/examBookings.api';
import {
  DataTable,
  type ActiveFilterChip,
  type DataTableColumn,
  type DataTableSortState,
} from '@/components/table/DataTable';
import { EmptyState } from '@/components/table/EmptyState';
import { ProgressBar } from '@/components/common/ProgressBar';
import { formatDateTime } from '@/lib/format';
import { BOOKING_STATUSES, EXAM_SECTIONS } from '@/types/shared';
import type {
  BookingStatus,
  ExamBookingResponse,
  ExamSection,
  PaginatedExamBookings,
} from '@/types/shared';
import './ExamBookingsListPage.css';

export function ExamBookingsListPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState<DataTableSortState>({ field: null, direction: 'asc' });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sectionFilter, setSectionFilter] = useState<ExamSection | ''>('');
  const [statusFilter, setStatusFilter] = useState<BookingStatus | ''>('');

  const [data, setData] = useState<PaginatedExamBookings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Debounce the free-text search box before it drives the `search` query param.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Any filter/search change re-starts pagination at page 1 (sort changes keep the current page).
  useEffect(() => {
    setPage(1);
  }, [search, sectionFilter, statusFilter]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const sortParam = sort.field ? `${sort.field}:${sort.direction.toUpperCase()}` : undefined;

    listExamBookings({
      page,
      pageSize,
      sort: sortParam,
      search: search || undefined,
      examSection: sectionFilter || undefined,
      bookingStatus: statusFilter || undefined,
    })
      .then((result) => {
        if (cancelled) return;
        setData(result);
      })
      .catch(() => {
        if (cancelled) return;
        setError(t('examBookings.error.loadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page, pageSize, sort, search, sectionFilter, statusFilter, t, reloadToken]);

  const activeFilterCount = [Boolean(search), sectionFilter !== '', statusFilter !== ''].filter(
    Boolean,
  ).length;
  const hasActiveFilters = activeFilterCount > 0;

  function clearFilters() {
    setSearchInput('');
    setSearch('');
    setSectionFilter('');
    setStatusFilter('');
  }

  const activeFilterChips: ActiveFilterChip[] = [
    sectionFilter !== '' && {
      key: 'examSection',
      label: t('examBookings.filters.examSection'),
      valueLabel: t(`examBookings.sections.${sectionFilter}`),
      onRemove: () => setSectionFilter(''),
    },
    statusFilter !== '' && {
      key: 'bookingStatus',
      label: t('examBookings.filters.bookingStatus'),
      valueLabel: t(`examBookings.status.${statusFilter}`),
      onRemove: () => setStatusFilter(''),
    },
  ].filter(Boolean) as ActiveFilterChip[];

  const columns: DataTableColumn<ExamBookingResponse>[] = [
    {
      key: 'studentCode',
      header: t('examBookings.columns.studentCode'),
      sortField: 'studentCode',
      // Primary identifier column — always rendered, never hidden (14-...-spec.md §4.2).
      toggleable: false,
      render: (row) => (
        <Link
          to={`/exam-bookings/${row.student.studentCode}`}
          className="fa-code-pill fa-ltr-isolate"
        >
          {row.student.studentCode}
        </Link>
      ),
    },
    {
      key: 'nameAr',
      header: t('examBookings.columns.nameAr'),
      sortField: 'nameAr',
      render: (row) => row.student.nameAr,
    },
    {
      key: 'nameEn',
      header: t('examBookings.columns.nameEn'),
      sortField: 'nameEn',
      render: (row) => row.student.nameEn,
    },
    {
      key: 'bookedCount',
      header: t('examBookings.columns.bookedCount'),
      render: (row) => (
        <span className="fa-exam-bookings-list__progress-cell">
          <ProgressBar
            value={row.bookedCount}
            max={row.sections.length}
            label={t('examBookings.bookedValue', {
              booked: row.bookedCount,
              total: row.sections.length,
            })}
          />
          <bdi>
            {t('examBookings.bookedValue', {
              booked: row.bookedCount,
              total: row.sections.length,
            })}
          </bdi>
        </span>
      ),
    },
    {
      key: 'audit',
      header: t('examBookings.columns.audit'),
      render: (row) => (
        <span className="fa-exam-bookings-list__audit">
          <span className="fa-exam-bookings-list__audit-name">
            {row.updatedBy?.fullName ?? t('examBookings.unknownUser')}
          </span>
          <bdi className="fa-exam-bookings-list__audit-date">
            {formatDateTime(row.updatedAt, i18n.language)}
          </bdi>
        </span>
      ),
    },
  ];

  return (
    <section className="fa-exam-bookings-list">
      <div className="fa-exam-bookings-list__header">
        <div>
          <h1 className="fa-exam-bookings-list__title">{t('examBookings.listTitle')}</h1>
          <p className="fa-exam-bookings-list__subtitle">
            {data ? t('examBookings.resultCount', { count: data.meta.total }) : ' '}
          </p>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.student.studentCode}
        caption={t('examBookings.tableCaption')}
        meta={data?.meta ?? null}
        loading={loading}
        error={error}
        onRetry={() => setReloadToken((v) => v + 1)}
        onRowClick={(row) => navigate(`/exam-bookings/${row.student.studentCode}`)}
        search={{
          value: searchInput,
          onChange: setSearchInput,
          placeholder: t('examBookings.searchPlaceholder'),
          ariaLabel: t('examBookings.searchAriaLabel'),
        }}
        sort={sort}
        onSortChange={setSort}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        exportConfig={{
          endpoint: '/exam-bookings/export',
          // Mirrors the list call's active filters, minus page/pageSize.
          params: {
            sort: sort.field ? `${sort.field}:${sort.direction.toUpperCase()}` : undefined,
            search: search || undefined,
            examSection: sectionFilter || undefined,
            bookingStatus: statusFilter || undefined,
          },
          fallbackFilename: 'ExamBookings_export.xlsx',
        }}
        columnVisibility={{
          storageKey: 'exam-bookings-table-columns',
          ariaLabel: t('table.columns.toggleButtonAriaLabel'),
          drawerTitle: t('table.columns.drawerTitleDefault'),
        }}
        activeFilters={activeFilterChips}
        onClearAllFilters={clearFilters}
        filtersDrawerTitle={t('examBookings.filters.groupLabel')}
        filtersSlot={
          <div className="fa-exam-bookings-list__filters-group">
            <label className="fa-exam-bookings-list__filter">
              <span className="fa-exam-bookings-list__filter-label">
                {t('examBookings.filters.examSection')}
              </span>
              <select
                value={sectionFilter}
                onChange={(e) => setSectionFilter(e.target.value as ExamSection | '')}
              >
                <option value="">{t('examBookings.filters.allSections')}</option>
                {EXAM_SECTIONS.map((section) => (
                  <option key={section} value={section}>
                    {t(`examBookings.sections.${section}`)}
                  </option>
                ))}
              </select>
            </label>

            <label className="fa-exam-bookings-list__filter">
              <span className="fa-exam-bookings-list__filter-label">
                {t('examBookings.filters.bookingStatus')}
              </span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as BookingStatus | '')}
              >
                <option value="">{t('examBookings.filters.allStatuses')}</option>
                {BOOKING_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {t(`examBookings.status.${status}`)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        }
        emptyState={
          hasActiveFilters ? (
            <EmptyState
              icon={BiSearch}
              title={t('examBookings.empty.noMatchesTitle')}
              hint={t('examBookings.empty.noMatchesHint')}
              action={
                <button type="button" className="fa-btn fa-btn--secondary" onClick={clearFilters}>
                  {t('examBookings.filters.clear')}
                </button>
              }
            />
          ) : (
            <EmptyState
              icon={BiSolidInbox}
              title={t('examBookings.empty.noRecordsTitle')}
              hint={t('examBookings.empty.noRecordsHint')}
            />
          )
        }
      />
    </section>
  );
}
